// ============================================
// LinkWave 统一布局组件
// 确保所有页面风格一致
// ============================================

import React from 'react';
import { LucideIcon, Search, Download, Plus } from 'lucide-react';

// ==================== 类型定义 ====================

interface PageHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  badge?: { label: string; color?: string };
  extra?: React.ReactNode;
}

interface TabNavProps<T extends string> {
  tabs: Array<{ id: T; label: string; icon?: LucideIcon; badge?: number }>;
  activeTab: T;
  onChange: (tab: T) => void;
}

interface FilterBarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters?: React.ReactNode;
  extra?: React.ReactNode;
}

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  change?: { value: string; positive: boolean };
  color?: 'red' | 'green' | 'blue' | 'purple' | 'default';
  highlight?: boolean;
}

interface ActionButtonProps {
  icon?: LucideIcon;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

// ==================== 颜色配置 ====================

const COLOR_MAP = {
  red: {
    bg: 'bg-[rgba(196,30,58,0.1)]',
    text: 'text-[#C41E3A]',
    border: 'border-[rgba(196,30,58,0.2)]',
    iconBg: 'gradient-brand',
  },
  green: {
    bg: 'bg-[rgba(34,139,34,0.1)]',
    text: 'text-[#228B22]',
    border: 'border-[rgba(34,139,34,0.2)]',
    iconBg: 'bg-gradient-to-br from-[#228B22] to-[#20B2AA]',
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
  },
  purple: {
    bg: 'bg-[rgba(139,71,137,0.1)]',
    text: 'text-[#8B4789]',
    border: 'border-[rgba(139,71,137,0.2)]',
    iconBg: 'bg-gradient-to-br from-[#8B4789] to-[#4A90A4]',
  },
  default: {
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
    iconBg: 'bg-gray-500',
  },
};

// ==================== 组件 ====================

// 页面头部
export const PageHeader: React.FC<PageHeaderProps> = ({ 
  icon: Icon, 
  title, 
  subtitle, 
  badge,
  extra 
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl gradient-brand flex items-center justify-center shadow-brand">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {badge && (
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${badge.color || 'bg-[rgba(196,30,58,0.1)] text-[#C41E3A] border-[rgba(196,30,58,0.2)]'}`}>
                {badge.label}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
    {extra && <div className="flex items-center gap-3">{extra}</div>}
  </div>
);

// Tab 导航
export const TabNav = <T extends string>({ tabs, activeTab, onChange }: TabNavProps<T>) => (
  <nav className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
    {tabs.map((tab) => {
      const Icon = tab.icon;
      const isActive = activeTab === tab.id;
      
      return (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            isActive
              ? 'gradient-brand text-white shadow-md'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && (
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
              isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {tab.badge}
            </span>
          )}
        </button>
      );
    })}
  </nav>
);

// 筛选栏
export const FilterBar: React.FC<FilterBarProps> = ({
  searchPlaceholder = '搜索...',
  searchValue,
  onSearchChange,
  filters,
  extra,
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      {filters}
    </div>
    <div className="flex items-center gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-64 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C41E3A] focus:ring-2 focus:ring-[rgba(196,30,58,0.1)] transition-all"
        />
      </div>
      {extra}
    </div>
  </div>
);

// 状态筛选按钮组
export const StatusFilter = <T extends string>({
  options,
  active,
  onChange,
}: {
  options: Array<{ id: T; label: string; count: number }>;
  active: T;
  onChange: (id: T) => void;
}) => (
  <div className="flex bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
    {options.map((opt) => (
      <button
        key={opt.id}
        onClick={() => onChange(opt.id)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          active === opt.id 
            ? 'bg-gray-900 text-white' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {opt.label} ({opt.count})
      </button>
    ))}
  </div>
);

// 统计卡片
export const StatCard: React.FC<StatCardProps> = ({ 
  icon: Icon, 
  label, 
  value, 
  change,
  color = 'default',
  highlight = false,
}) => {
  const colors = COLOR_MAP[color];
  
  return (
    <div className={`card ${highlight ? 'ring-2 ring-[#C41E3A]/20' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl ${colors.iconBg} flex items-center justify-center shadow-brand`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {change && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
            change.positive ? 'bg-[rgba(34,139,34,0.1)] text-[#228B22]' : 'bg-[rgba(239,68,68,0.1)] text-[#EF4444]'
          }`}>
            {change.positive ? '↑' : '↓'}
            {change.value}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
};

// 操作按钮
export const ActionButton: React.FC<ActionButtonProps> = ({ 
  icon: Icon, 
  children, 
  variant = 'secondary',
  onClick,
  disabled,
  className = '',
}) => {
  const baseClass = 'flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50';
  
  const variantClass = {
    primary: 'gradient-brand text-white shadow-brand hover:opacity-90',
    secondary: 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
  }[variant];
  
  return (
    <button className={`${baseClass} ${variantClass} ${className}`} onClick={onClick} disabled={disabled}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

// 导出按钮
export const ExportButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <ActionButton icon={Download} variant="secondary" onClick={onClick}>
    导出
  </ActionButton>
);

// 新增按钮
export const AddButton: React.FC<{ onClick?: () => void; children?: React.ReactNode }> = ({ 
  onClick, 
  children = '新增' 
}) => (
  <ActionButton icon={Plus} variant="primary" onClick={onClick}>
    {children}
  </ActionButton>
);

// 页面容器
export const PageContainer: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`p-8 max-w-7xl mx-auto space-y-6 ${className}`}>
    {children}
  </div>
);

// 卡片网格
export const CardGrid: React.FC<{ children: React.ReactNode; cols?: number; className?: string }> = ({ 
  children, 
  cols = 4,
  className = '' 
}) => (
  <div className={`grid gap-5 ${cols === 3 ? 'grid-cols-3' : cols === 2 ? 'grid-cols-2' : 'grid-cols-4'} ${className}`}>
    {children}
  </div>
);

// 内容卡片
export const ContentCard: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  title?: string;
  extra?: React.ReactNode;
}> = ({ children, className = '', title, extra }) => (
  <div className={`card ${className}`}>
    {(title || extra) && (
      <div className="flex items-center justify-between mb-4">
        {title && <h3 className="text-lg font-bold text-gray-900">{title}</h3>}
        {extra && <div>{extra}</div>}
      </div>
    )}
    {children}
  </div>
);

// 空状态
export const EmptyState: React.FC<{ 
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ icon: Icon, title, description, action }) => (
  <div className="py-16 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <p className="font-medium text-gray-900">{title}</p>
    {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// 加载状态
export const LoadingState: React.FC<{ message?: string }> = ({ message = '加载中...' }) => (
  <div className="py-16 text-center">
    <div className="w-12 h-12 border-3 border-gray-200 border-t-[#C41E3A] rounded-full animate-spin mx-auto mb-4" />
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// 分页器
export const Pagination: React.FC<{
  current: number;
  total: number;
  pageSize?: number;
  onChange?: (page: number) => void;
}> = ({ current, total, pageSize = 10, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  
  return (
    <div className="flex items-center justify-between py-4">
      <p className="text-sm text-gray-500">
        共 {total} 条记录，第 {current}/{totalPages} 页
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange?.(current - 1)}
          disabled={current <= 1}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
        >
          上一页
        </button>
        <button
          onClick={() => onChange?.(current + 1)}
          disabled={current >= totalPages}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
        >
          下一页
        </button>
      </div>
    </div>
  );
};

export default {
  PageHeader,
  TabNav,
  FilterBar,
  StatusFilter,
  StatCard,
  ActionButton,
  ExportButton,
  AddButton,
  PageContainer,
  CardGrid,
  ContentCard,
  EmptyState,
  LoadingState,
  Pagination,
};
